import {
  DatePickerType,
  DatePickerValue,
  MonthPickerInput,
  MonthPickerInputProps,
} from '@mantine/dates';
import { IconCalendarEvent } from '@tabler/icons-react';
import {
  EN_LOCALE_DATE_FORMAT,
  parseDateUnknown,
} from '@zorro/shared/formatters';
import { ForwardedRef, forwardRef } from 'react';

import { ZorroIcon } from '../../Icon';
import { brand } from '../../theme';
import { ReadOnlyLock } from '../../v2/ReadOnlyLock';
import {
  calendarDropdownStyles,
  calendarPanelProps,
  dateInputClassNames,
} from '../DateInput/calendarPanel';

export interface IMonthPickerInputProps<T extends DatePickerType> extends Omit<
  MonthPickerInputProps<T>,
  'onChange' | 'readOnly'
> {
  iconColor?: string;
  isReadOnly?: boolean;
  shouldUseEndOfMonth?: boolean;
  readOnlyVariant?: 'label' | 'lock';
  onChange: (value: DatePickerValue<T>) => void;
}

// TODO: Add storybook
export const ZorroMonthPickerInput = forwardRef(
  <T extends DatePickerType>(
    {
      placeholder,
      size = 'md',
      clearable = false,
      rightSection,
      value,
      onChange,
      type = 'default',
      iconColor = brand.zorroGray400,
      shouldUseEndOfMonth = false,
      isReadOnly,
      readOnlyVariant = 'label',
      ...props
    }: IMonthPickerInputProps<T>,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const handleChange = (newValue: DatePickerValue<T>) => {
      // Mantine v8 returns strings, but we need to pass Dates to the app
      // We accept that newValue coming here is technically strings despite the type signature from Mantine
      if (type === 'range') {
        const stringRange = newValue as [string | null, string | null];
        const dateRange: [Date | null, Date | null] = [
          stringRange[0] ? parseDateUnknown(stringRange[0]).toDate() : null,
          stringRange[1] ? parseDateUnknown(stringRange[1]).toDate() : null,
        ];

        if (dateRange[1]) {
          dateRange[1] = parseDateUnknown(dateRange[1]).endOf('month').toDate();
        }

        onChange?.(dateRange as DatePickerValue<T>);
      } else if (newValue !== null && shouldUseEndOfMonth) {
        const endOfMonthVal = parseDateUnknown(newValue as string)
          .endOf('month')
          .toDate();
        onChange?.(endOfMonthVal as DatePickerValue<T>);
      } else {
        const dateVal = newValue
          ? parseDateUnknown(newValue as string).toDate()
          : null;
        onChange?.(dateVal as DatePickerValue<T>);
      }
    };
    return (
      <MonthPickerInput
        placeholder={placeholder}
        size={size}
        valueFormat={EN_LOCALE_DATE_FORMAT}
        clearable={clearable}
        popoverProps={{ styles: calendarDropdownStyles }}
        readOnly={isReadOnly}
        data-readonly-variant={
          isReadOnly && readOnlyVariant === 'lock' ? 'lock' : undefined
        }
        rightSection={
          rightSection ||
          (isReadOnly && readOnlyVariant === 'lock' ? (
            <ReadOnlyLock size={20} stroke={1.5} color={iconColor} />
          ) : (
            <ZorroIcon icon={IconCalendarEvent} color={iconColor} size={20} />
          ))
        }
        classNames={dateInputClassNames}
        onChange={handleChange}
        value={value}
        type={type}
        ref={ref}
        {...calendarPanelProps}
        {...props}
      />
    );
  }
);

ZorroMonthPickerInput.displayName = 'ZorroMonthPickerInput';
